import { useEffect, useMemo, useState } from 'react';
import type { OccurrenceStepEnum } from '../../enums/occurrenceStep';
import useAssets from '../../hooks/useAssets';
import useOccurrence from '../../hooks/useOccurrence';
import usePartner from '../../hooks/usePartner';
import type { Occurrence, OccurrenceWithTimeSpent } from '../../models/occurrence';
import ExportProgressKanbanDialog from '../exportProgressKanbanDialog';
import CopyTokenButton from '../kanban/components/copyTokenButton';
import { OccurrenceImport } from '../kanban/components/occurrence-import';
import NewOccurrenceFormDialog from '../kanban/dialogs/newOccurrenceFormDialog';
import { UptimePager } from '../shared';
import { useClientOptions, useFilterOptions } from './hooks/useFilterOptions';
import useRepairGuard from './hooks/useRepairGuard';
import useSelectedOccurrence from './hooks/useSelectedOccurrence';
import useTrackingChips from './hooks/useTrackingChips';
import useTrackingExport from './hooks/useTrackingExport';
import useTrackingFilters from './hooks/useTrackingFilters';
import useTrackingLoadState from './hooks/useTrackingLoadState';
import useTrackingPaging from './hooks/useTrackingPaging';
import useTrackingSummary from './hooks/useTrackingSummary';
import { getTrackingPersonaConfig } from './personaConfig';
import { type DateRange, type SearchField, type TrackingPersona, getStepLabelId } from './trackingUtils';
import OccurrenceDrawer from './ui/OccurrenceDrawer';
import TrackingBoard from './ui/TrackingBoard';
import TrackingChips from './ui/TrackingChips';
import TrackingKanban from './ui/TrackingKanban';
import TrackingMoreFilters from './ui/TrackingMoreFilters';
import TrackingSummary from './ui/TrackingSummary';
import TrackingTable from './ui/TrackingTable';
import TrackingToolbar from './ui/TrackingToolbar';
import useMtText from './ui/useMtText';
import '../shared/styles/tokens.scss';
import './maintenanceTracking.scss';

interface IMaintenanceTrackingProps {
    persona: TrackingPersona;
    chassisList?: string[];
    onOccurrenceListsReady?: (current: OccurrenceWithTimeSpent[], finalized: OccurrenceWithTimeSpent[]) => void;
    initialDateFilters?: {
        startDateFilter?: DateRange | null;
        updateDateFilter?: DateRange | null;
    };
    onDateFiltersChange?: (filters: { startDateFilter: DateRange | null; updateDateFilter: DateRange | null }) => void;
}

const PROGRESSIVE_LOAD = { progressiveLoad: true };

const MaintenanceTracking = ({
    persona,
    chassisList,
    onOccurrenceListsReady,
    initialDateFilters,
    onDateFiltersChange,
}: IMaintenanceTrackingProps) => {
    const { t, msg, formatNumber } = useMtText();
    const config = getTrackingPersonaConfig(persona);
    const { allAssets, getAssets } = useAssets();
    const [assetsLoading, setAssetsLoading] = useState(() => !allAssets.length);
    const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [sortByStep, setSortByStep] = useState('TICKE');
    const [showImport, setShowImport] = useState(false);
    const [showNewOccurrence, setShowNewOccurrence] = useState(false);
    const [newOccurrence, setNewOccurrence] = useState<Occurrence>({} as Occurrence);

    useEffect(() => {
        if (allAssets.length) {
            setAssetsLoading(false);
            return;
        }
        getAssets().finally(() => setAssetsLoading(false));
    }, []);

    const fleetAssetIds = useMemo(
        () => (persona === 'PARTNERSHIP' ? undefined : allAssets.map(asset => asset.id)),
        [allAssets, persona]
    );

    const occurrences = useOccurrence(
        persona,
        undefined,
        fleetAssetIds,
        chassisList,
        order,
        sortByStep,
        initialDateFilters,
        onDateFiltersChange,
        PROGRESSIVE_LOAD
    );
    const {
        finishedFilter,
        stepFilter,
        chassisListFilterByStep,
        stepTypeCurrentFilter,
        searchFieldName,
        searchFieldValue,
        occurrenceList,
        occurrenceWithTimeSpentList,
        finalizedOccurrenceWithTimeSpentList,
        rawCurrentOccurrenceWithTimeSpentList,
        totalOccurrences,
        totalInProgress,
        loading,
        loadingTimeSpent,
        loadingMoreData,
        getOccurrenceList,
        refreshCurrentData,
        reloadOccurrenceList,
        occurrenceStepAvailable: stepKeys,
        steps,
        isValidStep,
        getNotFilledRequiredFields,
        create,
        save,
        changeStep,
        finish,
        review,
        reviewStep,
        getReview,
        upsertDispatch,
        cancelDispatch,
        removeDispatch,
    } = occurrences;

    const { partners } = usePartner();
    const { guardedChangeStep, repairDialog } = useRepairGuard(changeStep, save);
    const { cpvOptions, modelsByType, modelTypes, modelOptions } = useFilterOptions();
    const filters = useTrackingFilters({ occurrences, modelsByType });
    const { finished, status, activeStage, criticality, alertOnly, period, currentStageMode, more } = filters;
    const { chips, moreFiltersCount } = useTrackingChips(occurrences, filters);
    const clientOptions = useClientOptions({
        selectedClients: more.clients,
        finished,
        currentList: rawCurrentOccurrenceWithTimeSpentList,
        finalizedList: finalizedOccurrenceWithTimeSpentList,
    });

    useEffect(() => {
        onOccurrenceListsReady?.(rawCurrentOccurrenceWithTimeSpentList, finalizedOccurrenceWithTimeSpentList);
    }, [rawCurrentOccurrenceWithTimeSpentList, finalizedOccurrenceWithTimeSpentList]);

    const listLoading = assetsLoading || loading;
    const paging = useTrackingPaging({
        occurrenceList,
        totalOccurrences,
        getOccurrenceList,
        listLoading,
        loadingMoreData,
    });
    const { view, pageRows } = paging;
    const { loadedOnce, statsLoadedOnce, statsLoading, stageList } = useTrackingLoadState({
        listLoading,
        loadingTimeSpent,
        stepTypeCurrentFilter,
        currentList: occurrenceWithTimeSpentList,
    });
    const { selectedId, selectedOccurrence, openOccurrence, closeOccurrence, refreshDetached } = useSelectedOccurrence(
        occurrenceList,
        persona
    );
    const { handleExport, dialogProps: exportDialogProps } = useTrackingExport(occurrences, persona);

    const { bigNumbers, lateCount, stages } = useTrackingSummary({
        totalOccurrences,
        totalInProgress,
        currentList: occurrenceWithTimeSpentList,
        stageList,
        currentStageMode,
        finalizedList: finalizedOccurrenceWithTimeSpentList,
        finishedFilter,
        stepFilter,
        chassisListFilterByStep,
        stepKeys,
        steps,
    });

    const timeSpentById = useMemo(() => {
        const map = new Map<string, OccurrenceWithTimeSpent>();
        for (const item of [...rawCurrentOccurrenceWithTimeSpentList, ...finalizedOccurrenceWithTimeSpentList]) {
            map.set(item.occurrenceId, item);
        }
        return map;
    }, [rawCurrentOccurrenceWithTimeSpentList, finalizedOccurrenceWithTimeSpentList]);

    const plateByChassis = useMemo(() => {
        const map = new Map<string, string>();
        for (const asset of allAssets) {
            const plate = asset.licensePlate?.trim();
            if (asset.chassis && plate && plate !== '-') {
                map.set(asset.chassis, plate);
            }
        }
        return map;
    }, [allAssets]);

    const stepCardProps = {
        reloadOccurrenceList,
        partners,
        isValidStep,
        loading,
        save: (occurrence: Occurrence) => save(occurrence).then(refreshDetached),
        changeStep: (occurrence: Occurrence, fromStepId: number, to: OccurrenceStepEnum) =>
            guardedChangeStep(occurrence, fromStepId, to).then(refreshDetached),
        getNotFilledRequiredFields,
        finishOccurrence: (
            occurrence: Occurrence,
            reasonType?: string,
            reasonDescription?: string,
            fromStepId?: number
        ) => finish(occurrence, reasonType, reasonDescription, fromStepId).then(refreshDetached),
        disabledAllFields: config.readOnly,
        persona,
        review,
        reviewStep,
        getReview,
        occurrenceStepAvailable: stepKeys,
        upsertDispatch,
        cancelDispatch,
        removeDispatch,
        steps,
        onRefresh: async () => {
            await refreshCurrentData();
            await refreshDetached(undefined);
        },
    };

    const summaryProps = {
        loading,
        persona,
        disabledAllFields: config.readOnly,
        occurrenceStepAvailable: stepKeys,
        partners,
        changeStep: guardedChangeStep,
        getNotFilledRequiredFields,
        save,
        isValidStep,
    };

    const emptyMessage = chips.length ? t('results.emptyFiltered') : t('results.empty');

    return (
        <div className='maintenance-tracking'>
            <div className='mt-panel'>
                <TrackingToolbar
                    status={status}
                    onStatusChange={filters.changeStatus}
                    searchField={(searchFieldName as SearchField) || 'CHASSIS'}
                    searchValue={searchFieldValue}
                    criticality={criticality}
                    alertOnly={alertOnly}
                    period={period}
                    {...filters.toolbarHandlers}
                    moreFiltersCount={moreFiltersCount}
                    onOpenMoreFilters={() => filters.setShowMoreFilters(true)}
                    actions={
                        <>
                            <CopyTokenButton />
                            {config.canCreateOccurrence && (
                                <button
                                    type='button'
                                    className='mt-btn mt-primary'
                                    onClick={() => {
                                        setNewOccurrence({} as Occurrence);
                                        setShowImport(true);
                                    }}
                                >
                                    <span className='rioglyph rioglyph-plus' />
                                    {msg('intl-msg:kanban.newOccurrenceButton.createTicket')}
                                </button>
                            )}
                        </>
                    }
                />
                <TrackingChips
                    view={view}
                    onViewChange={paging.changeView}
                    viewActions={
                        <button
                            type='button'
                            className='mt-btn'
                            disabled={listLoading || !totalOccurrences}
                            onClick={handleExport}
                        >
                            <span className='rioglyph rioglyph-download' />
                            {t('results.export')}
                        </button>
                    }
                    chips={chips}
                    onClearAll={filters.resetAll}
                    onApplyView={filters.applySavedView}
                />
            </div>

            <TrackingSummary
                bigNumbers={bigNumbers}
                lateCount={lateCount}
                finished={finished}
                firstLoad={!statsLoadedOnce}
                refreshing={statsLoading}
                stages={stages}
                showStages={view === 'table'}
                activeStage={activeStage}
                onStageClick={filters.toggleStage}
            />

            <div className='mt-panel mt-results'>
                <div className='mt-reshead'>
                    {loadedOnce ? (
                        <strong className={listLoading ? 'mt-stale' : undefined}>
                            {t('results.count', { count: formatNumber(totalOccurrences) })}
                        </strong>
                    ) : (
                        <span className='mt-skel' style={{ width: 120, height: 18 }} />
                    )}
                    {stepTypeCurrentFilter ? (
                        <span className='mt-muted'>
                            {t('results.inStage', { stage: msg(getStepLabelId(stepTypeCurrentFilter)) })}
                        </span>
                    ) : (
                        stepFilter && (
                            <span className='mt-muted'>
                                {t('results.passedStage', { stage: msg(getStepLabelId(stepFilter)) })}
                            </span>
                        )
                    )}
                    {(listLoading || paging.pageWaiting) && (
                        <output className='mt-loading' aria-live='polite'>
                            <span>{assetsLoading ? t('loading.vehicles') : t('loading.occurrences')}</span>
                            <span className='mt-progressbar'>
                                <i />
                            </span>
                        </output>
                    )}
                </div>

                {view === 'table' && (
                    <TrackingTable
                        rows={pageRows}
                        stepKeys={stepKeys}
                        steps={steps}
                        timeSpentById={timeSpentById}
                        loading={listLoading}
                        selectedId={selectedId}
                        onOpen={occurrence => openOccurrence(occurrence.id)}
                        emptyMessage={emptyMessage}
                    />
                )}

                {view === 'board' && (
                    <TrackingBoard
                        items={finished ? [] : occurrenceWithTimeSpentList}
                        stages={stages}
                        plateByChassis={plateByChassis}
                        activeStage={activeStage}
                        onStageClick={filters.toggleStage}
                        onOpen={openOccurrence}
                        emptyMessage={finished ? t('board.onlyOpen') : emptyMessage}
                    />
                )}

                {view === 'kanban' &&
                    (pageRows.length || listLoading ? (
                        <TrackingKanban
                            rows={pageRows}
                            stepKeys={stepKeys}
                            stages={stages}
                            timeSpentById={timeSpentById}
                            loading={listLoading}
                            activeStage={activeStage}
                            onStageClick={filters.toggleStage}
                            sortKey={sortByStep}
                            sortDirection={order}
                            onSort={(key, direction) => {
                                setSortByStep(key);
                                setOrder(direction);
                            }}
                            stepCardProps={stepCardProps}
                            summaryProps={summaryProps}
                        />
                    ) : (
                        <div className='mt-empty'>{emptyMessage}</div>
                    ))}

                {view !== 'board' && totalOccurrences > 0 && !listLoading && (
                    <UptimePager
                        page={paging.uiPage}
                        pageSize={paging.pageSize}
                        total={totalOccurrences}
                        onPageChange={paging.changePage}
                        onPageSizeChange={paging.changePageSize}
                        pageSizes={paging.pageSizes}
                    />
                )}
            </div>

            {filters.showMoreFilters && (
                <TrackingMoreFilters
                    value={filters.drawerValue}
                    cpvOptions={cpvOptions}
                    modelTypes={modelTypes}
                    modelOptions={modelOptions}
                    clientOptions={clientOptions}
                    stepKeys={stepKeys}
                    onApply={filters.applyMoreFilters}
                    onClose={() => filters.setShowMoreFilters(false)}
                />
            )}

            {selectedOccurrence && (
                <OccurrenceDrawer
                    key={selectedOccurrence.id}
                    occurrence={selectedOccurrence}
                    timeSpent={timeSpentById.get(selectedOccurrence.id)}
                    stepKeys={stepKeys}
                    stepCardProps={stepCardProps}
                    summaryProps={summaryProps}
                    onClose={closeOccurrence}
                />
            )}

            <OccurrenceImport
                show={showImport}
                onClose={() => setShowImport(false)}
                onCreateSingleOccurrence={() => {
                    setShowImport(false);
                    setShowNewOccurrence(true);
                }}
            />

            <NewOccurrenceFormDialog
                occurrence={newOccurrence}
                setOccurrence={setNewOccurrence}
                showDialog={showNewOccurrence}
                onClose={() => setShowNewOccurrence(false)}
                partners={partners}
                create={create}
                isValidStep={isValidStep}
                persona={persona}
            />

            <ExportProgressKanbanDialog {...exportDialogProps} />

            {repairDialog}
        </div>
    );
};

export default MaintenanceTracking;
